/**
 * Per-connector toggle list rendered inside ConnectorPickerButton's
 * popover. One row per ACTIVE Connection — toolkit logo, name,
 * external account label, and a switch-style Toggle the agent reads
 * via the chat-message multipart `enabled_connectors` field.
 *
 * The Toggle component is colocated with ConnectorPickerButton (which
 * passes it as a render prop here) so the visual style stays in lockstep
 * across the web-search row and the connector rows.
 */

import { Box, HStack, Text, VStack } from '@chakra-ui/react';
import type { ComponentType } from 'react';
import type { ConnectionSummary, ToolkitCatalogEntry } from '@bower/shared';
import {
  workspaceConnectorPresentationFor,
  type WorkspaceConnectorPresentation,
} from '../../../constants/workspaceConnectors';
import { MaterialIcon } from '../../ui/MaterialIcon';
import { ConnectorBrandIcon } from '../../ui/brand/ConnectorBrandIcon';

interface ToggleProps {
  readonly checked: boolean;
  readonly onClick: () => void;
  readonly ariaLabel: string;
}

interface ConnectorListProps {
  readonly connections: ReadonlyArray<ConnectionSummary>;
  readonly catalogByKey: ReadonlyMap<string, ToolkitCatalogEntry>;
  readonly workspaceId: string | null;
  readonly isEnabled: (workspaceId: string, toolkitKey: string) => boolean;
  readonly onToggle: (toolkitKey: string) => void;
  readonly onOpenSettings: () => void;
  readonly Toggle: ComponentType<ToggleProps>;
}

function WorkspaceServiceMarks({ toolkitKey }: { readonly toolkitKey: string }) {
  const presentation = workspaceConnectorPresentationFor(toolkitKey);
  if (!presentation) return null;

  return (
    <HStack spacing={1} mt={1} aria-hidden>
      {presentation.capabilities.map((capability) => (
        <ConnectorBrandIcon key={capability.productName} name={capability.icon} boxSize={3.5} />
      ))}
    </HStack>
  );
}

function ConnectorRowIcon({
  presentation,
  toolkit,
}: {
  readonly presentation: WorkspaceConnectorPresentation | undefined;
  readonly toolkit: ToolkitCatalogEntry | undefined;
}) {
  if (presentation) {
    return <ConnectorBrandIcon name={presentation.icon} boxSize="20px" flexShrink={0} />;
  }
  if (toolkit?.iconUrl) {
    return (
      <Box as="img" src={toolkit.iconUrl} alt="" boxSize="20px" borderRadius="sm" flexShrink={0} />
    );
  }
  return <MaterialIcon name="hub" boxSize={5} color="text.muted" />;
}

export function ConnectorList({
  connections,
  catalogByKey,
  workspaceId,
  isEnabled,
  onToggle,
  onOpenSettings,
  Toggle,
}: ConnectorListProps) {
  return (
    <VStack spacing={0} align="stretch">
      {connections.map((conn) => {
        const toolkit = catalogByKey.get(conn.toolkitKey);
        const presentation = workspaceConnectorPresentationFor(conn.toolkitKey);
        const enabled = workspaceId ? isEnabled(workspaceId, conn.toolkitKey) : true;
        const toolkitName = presentation?.name ?? toolkit?.name ?? conn.toolkitKey;
        const subline = presentation?.chatSummary ?? conn.externalAccountLabel;
        return (
          <HStack
            key={conn.id}
            spacing={3}
            py={presentation ? 2.5 : 2}
            px={presentation ? 2 : 1}
            borderRadius="lg"
            bg={presentation ? 'bg.subtle' : 'transparent'}
            borderWidth={presentation ? '1px' : 0}
            borderColor="border.subtle"
            _hover={{ bg: 'bg.hover' }}
          >
            <ConnectorRowIcon presentation={presentation} toolkit={toolkit} />
            <Box flex={1} minW={0}>
              <Text fontSize="sm" color="text.primary" noOfLines={1}>
                {toolkitName}
              </Text>
              {subline && (
                <Text fontSize="2xs" color="text.muted" noOfLines={1}>
                  {subline}
                </Text>
              )}
              <WorkspaceServiceMarks toolkitKey={conn.toolkitKey} />
            </Box>
            <Toggle
              checked={enabled}
              onClick={() => onToggle(conn.toolkitKey)}
              ariaLabel={`Toggle ${toolkitName} in this chat`}
            />
          </HStack>
        );
      })}
      <HStack
        as="button"
        onClick={onOpenSettings}
        spacing={2}
        justify="flex-start"
        py={2}
        px={1}
        mt={1}
        borderRadius="md"
        _hover={{ bg: 'bg.hover' }}
        aria-label="Manage connectors in Settings"
      >
        <MaterialIcon name="settings" boxSize={4} color="text.muted" />
        <Text fontSize="xs" color="text.muted">
          Manage connectors
        </Text>
      </HStack>
    </VStack>
  );
}
